import { css } from '@flipster/styled-system/css';
import { token } from '@flipster/styled-system/tokens';
import Num from '@flipster/utils/num';

const getChartColor = (first: string, last: string) => {
  if (Num(last).minus(first).isZero()) return token('colors.content.default.level-5');
  if (Num(last).minus(first).isPositive()) return token('colors.content.state.positive');
  if (Num(last).minus(first).isNegative()) return token('colors.content.state.negative');
};

const calculateYValues = (plots: string[]) => {
  const newPlots = plots.map((p) => Number(p));
  const highest = Math.max(...newPlots);
  const lowest = Math.min(...newPlots);
  const mid = (highest + lowest) / 2;
  const ratio = highest !== lowest ? (highest - lowest) / 2 : 1;

  return newPlots.map((p) => (p - mid) / ratio);
};

const ChartPaddingRight = 24;
const ChartPaddingY = 16;

interface Props {
  chartData: string[];
  maxPointCount: number;
  width: number;
  height: number;
  chartMinHeight: number;
}

const TradeLineChart = ({ chartData, maxPointCount, width, height, chartMinHeight }: Props) => {
  const chartColor = getChartColor(chartData[0], chartData[chartData.length - 1]);
  const yValues = calculateYValues(chartData);

  const getPointX = (index: number, lastIndex: number) =>
    (index / lastIndex) * (((width - ChartPaddingRight) / maxPointCount) * chartData.length);
  const getPointY = (y: number) => (height === 0 ? 0 : height / 2 - (y * (height - 2 * ChartPaddingY)) / 2);

  const lastIndex = chartData.length ? chartData.length - 1 : 0;
  const lastX = chartData.length > 1 ? getPointX(lastIndex, lastIndex) : 0;
  const lastY = chartData.length ? getPointY(yValues[lastIndex]) : 0;

  return (
    <div className={css({ height: '100%', minHeight: chartMinHeight })}>
      <div className={css({ width: '100%', height: '100%' })}>
        <svg className={css({ width: '100%', height: '100%' })} height={height}>
          <polyline
            fill='none'
            stroke={chartColor}
            strokeWidth='2'
            strokeLinecap='round'
            points={yValues.map((y, i) => `${getPointX(i, lastIndex)},${getPointY(y)}`).join(' ')}
          />
          <circle cx={lastX} cy={lastY} r='4' fill={chartColor} />
          <circle cx={lastX} cy={lastY} r='8' fill={chartColor} opacity='0.2'>
            <animate attributeName='opacity' values='0;0.4;0' dur='2s' repeatCount='indefinite' />
          </circle>
        </svg>
      </div>
    </div>
  );
};

export default TradeLineChart;
